/**
 * shared/state.ts —— 页面级 UI 状态
 * 来历：一期 [SEC:state] 段的 state 对象，去掉两处后平移。
 *
 * 🔴 与一期的两处关键差别，别照搬一期：
 * 1. **`state.auth` 已删除**。一期它是 sessionStorage 里的演示登录门面；
 *    二期登录态的唯一真相是 `/api/auth/me`，缓存在 shared/meta.ts。
 *    在这里放一份登录态，就等于给「前端隐藏当权限」开了门（红线 4）。
 * 2. **`rp.no` 不再初始化为 CUR_NO**。一期 CUR_NO 写死为 3；二期当前期可能【根本不存在】
 *    （空当态，红线 3.5），初值一律 null，由路由守卫从 meta 填。
 *
 * 一条纪律：**这里只存"看的姿势"，不存业务数据**。
 * 排序、筛选、滚动位置、展开状态可以放；队伍列表、周报正文一律不放——
 * 那些是 load() 每次现取的，存进来就会与后端不同步（红线 2 的前端形态）。
 */

export interface UiState {
  /** 当前视图，由 router 写入 */
  view: string;
  /** 队伍详情页的对象 id */
  team: string | null;
  /** 详情页从哪来（overview / teams），决定面包屑与返回目标 */
  from: string;
  /** 返回列表时要高亮的行 */
  hit: string | null;

  /**
   * 登录页自己的瞬时状态：err 当前错误文案、field 出错字段、pwd 是否明文、acct 回填的账号、
   * ok 一次性成功提示（改完密码回登录页那句）、mode 本页当前渲染哪张表单。
   * ⚠ 密码本身【永远不进 state】——只在提交那一瞬间从 DOM 里读一次。
   */
  lg: { err: string; field: string; pwd: boolean; acct: string; ok: string; mode: 'login' | 'pwd' };

  /**
   * 运营总览：排序 / 方向 / 滚动。
   * 🔴 **没有 `tint` / `filter`**——着色切换器与四个筛选 chip 已于 2026-08-20 随
   *   「矩阵工具栏整行」一并删除（`02` §5.1）。**不要补回来**，同 `ck` 的 `type`。
   * ⚠ 排序本身在服务端做，这里存的只是「用户选了哪一列、哪个方向」。
   */
  ov: { sort: string; dir: 'asc' | 'desc'; scroll: number };

  /** 队伍管理 */
  tm: { sort: string; dir: 'asc' | 'desc'; q: string; arch: boolean; scroll: number };

  /** 团队 OKR：主从两栏各自独立滚动 */
  ok: { team: string | null; sort: string; scroll: number };

  /**
   * 双周报三页式（列表 / 详情 / 编辑）：
   *   no    = 当前浏览的期号，来自 hash 的 p 段；⚠ 初值 null，空当期时可能一直是 null
   *   team  = 详情页与编辑页的对象，列表页为 null
   *   edit  = 是否在编辑页（草稿、脏检查、离开拦截都只在它为 true 时生效）
   *   draft / base / snap = 编辑页的草稿、脏检查基准、进页时的 KR 快照
   *   dkey  = 草稿所属的「队伍|期号」，守卫靠它判断要不要重建草稿
   * ⚠ 换队或换期才重建草稿与快照；同一支队同一期【原地刷新不重建】，
   *   否则「打开 → 去 OKR 改 KR → 回来提交」这条版本冲突路径永远走不出来（一期 2026-08-04 定）。
   */
  rp: {
    no: number | null;
    team: string | null;
    edit: boolean;
    scroll: number;
    draft: unknown;
    base: string;
    snap: unknown;
    dkey: string;
  };

  /** 日常打卡：队伍来自 hash（进入时预置），其余两项只是页面状态。
   *  ⚠ 【没有 type】——活动类型已于 2026-08-20 整体删除（筛选下拉也一并去掉），**不要补回来** */
  ck: { team: string; span: string; q: string; scroll: number };

  /** 管理后台：滚动位置按 tab 分开存，否则从周期表切到人员表会停在莫名其妙的位置 */
  ad: {
    tab: string;
    q: string;
    type: string;
    st: string;
    sort: string;
    dir: 'asc' | 'desc';
    scroll: Record<string, number>;
  };

  /**
   * 管理后台 · 活跃统计（2026-09-15）。只存「用户选了什么」，数字全在服务端出参里（红线 4）。
   *   preset / from / to / grain  查询条件，改了要重新请求
   *   q / show / sort / dir       只作用于用户表的显示筛选，不重新请求、不改上方全站数字
   *   openFeat / openUsers        展开了哪个功能、哪几个人（纯瞬时状态）
   * ⚠ from / to 只在 preset = custom 时生效，初值空串：进页默认「本月」
   */
  an: {
    preset: string;
    from: string;
    to: string;
    grain: string;
    q: string;
    show: '' | 'on' | 'off';
    sort: string;
    dir: 'asc' | 'desc';
    openFeat: string | null;
    openUsers: string[];
  };

  /**
   * 项目快报（2026-08-25 整体重做）。
   *
   * 🔴 **这里只剩"看的姿势"**。一期那套 `scope / blocks / pics / edited / txt` 已全部作废：
   *   - `scope` —— 单队快报砍掉了，范围那一维不存在了；
   *   - `blocks` / `pics` / `edited` / `txt` —— 快报**落库**了，这些是**服务端的数据**，
   *     放在前端状态里就是第二份真相（本文件那条纪律的原话：业务数据一律不放）。
   *
   *   no     当前周期号，来自 hash；⚠ 初值 null，一期都没建时会一直是 null
   *   hist   历史版本浮层是否展开（纯瞬时状态，换期清掉）
   *   busy   正在生成中（按钮禁用、覆盖层显示）；⚠ 不落库、不进 hash
   */
  bl: {
    no: number | null;
    hist: boolean;
    busy: boolean;
    scroll: number;
  };
}

export const state: UiState = {
  view: 'overview',
  team: null,
  from: 'overview',
  hit: null,

  lg: { err: '', field: '', pwd: false, acct: '', ok: '', mode: 'login' },
  ov: { sort: 'id', dir: 'asc', scroll: 0 },
  tm: { sort: 'id', dir: 'asc', q: '', arch: false, scroll: 0 },
  ok: { team: null, sort: 'id', scroll: 0 },
  rp: { no: null, team: null, edit: false, scroll: 0, draft: null, base: '', snap: null, dkey: '' },
  ck: { team: '', span: '14', q: '', scroll: 0 },
  ad: { tab: 'periods', q: '', type: '', st: '', sort: 'id', dir: 'asc', scroll: { periods: 0, persons: 0, analytics: 0 } },
  an: { preset: 'month', from: '', to: '', grain: 'day', q: '', show: '', sort: '', dir: 'desc', openFeat: null, openUsers: [] },
  bl: { no: null, hist: false, busy: false, scroll: 0 },
};
